import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Beaker, Calendar, DollarSign, Sparkles, Zap,
  User, LogOut, LayoutDashboard
} from "lucide-react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { getCurrencySymbol, detectUserCountryCode } from "@/utils/currency";
import { PricingSection } from "@/components/landing/PricingSection";
import { FeaturesSection, HowItWorksSection, TrustSection } from "@/components/landing/MarketingSections";
import { GoalForm } from "@/components/landing/GoalForm";
import { GenerationOverlay } from "@/components/landing/GenerationOverlay";
import { TrialBanner } from "@/components/landing/TrialBanner";

const goalKeys = [
  { value: "fat-loss", labelKey: "fatLoss", icon: "🔥" },
  { value: "muscle-building", labelKey: "muscleBuilding", icon: "💪" },
  { value: "fat-burn-lean-muscle", labelKey: "fatBurnLeanMuscle", icon: "🔥💪", highlight: true },
  { value: "endurance-recovery", labelKey: "enduranceRecovery", icon: "🏃🩹", highlight: true },
  { value: "anti-aging", labelKey: "antiAging", icon: "✨" },
  { value: "recovery", labelKey: "recovery", icon: "🩹" },
  { value: "sleep", labelKey: "sleep", icon: "😴" },
  { value: "healing", labelKey: "healing", icon: "🏥" },
];

const englishGoalLabels = {
  "fat-loss": "Fat Loss / Weight Loss",
  "muscle-building": "Muscle Building / Bulking",
  "fat-burn-lean-muscle": "Fat Burn + Increase Lean Muscle",
  "endurance-recovery": "Improve Endurance + Recovery",
  "anti-aging": "Anti-Aging / Longevity",
  "recovery": "Recovery / Healing",
  "sleep": "Sleep Optimization",
  "healing": "Injury Healing"
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, getAuthHeader } = useAuth();
  const { t } = useLanguage();
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [customGoal, setCustomGoal] = useState("");
  const [weight, setWeight] = useState("80");
  const [weightUnit, setWeightUnit] = useState(() => localStorage.getItem("weightUnit") || "kg");
  const [experience, setExperience] = useState("beginner");
  const [loading, setLoading] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const { language } = useLanguage();

  // Persist unit choice + auto-convert visible weight when switching units
  const handleUnitChange = (nextUnit) => {
    if (nextUnit === weightUnit) return;
    const numeric = parseFloat(weight);
    if (!Number.isNaN(numeric) && numeric > 0) {
      const converted = nextUnit === "lbs"
        ? Math.round(numeric * 2.20462 * 10) / 10   // kg → lbs
        : Math.round(numeric / 2.20462 * 10) / 10;  // lbs → kg
      setWeight(String(converted));
    }
    setWeightUnit(nextUnit);
    localStorage.setItem("weightUnit", nextUnit);
  };

  useEffect(() => {
    const storedCode = localStorage.getItem('user_country_code');
    const detectedCode = storedCode || detectUserCountryCode();
    setCurrencySymbol(getCurrencySymbol(detectedCode));
  }, []);

  const goals = goalKeys.map(g => ({ ...g, label: t(g.labelKey) }));

  const features = [
    { icon: Sparkles, title: t("aiPoweredAnalysis"), description: t("aiAnalysisDesc") },
    { icon: Beaker, title: t("preciseDosing"), description: t("preciseDosingDesc") },
    { icon: Calendar, title: t("cycleCalendar"), description: t("cycleCalendarDesc") },
    { icon: DollarSign, title: t("costCalculator"), description: t("costCalculatorDesc") },
  ];

  const steps = [
    { number: "01", title: t("step1Title"), description: t("step1Desc") },
    { number: "02", title: t("step2Title"), description: t("step2Desc") },
    { number: "03", title: t("step3Title"), description: t("step3Desc") },
    { number: "04", title: t("step4Title"), description: t("step4Desc") },
  ];

  const toggleGoal = (goalValue) => {
    setSelectedGoals(prev =>
      prev.includes(goalValue)
        ? prev.filter(g => g !== goalValue)
        : [...prev, goalValue]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let finalGoal = "";

    if (selectedGoals.length === 0 && !customGoal.trim()) {
      toast.error(t("pleaseSelectGoal"));
      return;
    }

    if (selectedGoals.length > 0) {
      const goalLabels = selectedGoals.map(v => englishGoalLabels[v]).filter(Boolean);
      finalGoal = goalLabels.join(" + ");
    }

    if (customGoal.trim()) {
      finalGoal = finalGoal ? `${finalGoal} + ${customGoal.trim()}` : customGoal.trim();
    }

    setLoading(true);
    const currentLanguage = localStorage.getItem('preferredLanguage') || 'en';
    const headers = isAuthenticated ? getAuthHeader() : {};

    // Backend expects kg — convert if user entered lbs
    const weightNum = parseFloat(weight) || 0;
    const weightKg = weightUnit === "lbs" ? weightNum / 2.20462 : weightNum;

    try {
      // Step 1: Enqueue the generation job (returns immediately)
      const enqueueRes = await api.post('/generate-cycle', {
        goal: finalGoal,
        weight_kg: Math.round(weightKg * 10) / 10 || 80,
        experience_level: experience,
        language: currentLanguage,
      }, { headers, timeout: 15000 });

      const jobId = enqueueRes.data.job_id;
      if (!jobId) throw new Error("No job_id returned from server");

      // Step 2: Poll for status until done/failed (5-min ceiling)
      const startTs = Date.now();
      const MAX_WAIT_MS = 5 * 60 * 1000;
      const POLL_INTERVAL_MS = 3000;

      let finalResult = null;
      while (true) {
        if (Date.now() - startTs > MAX_WAIT_MS) {
          throw new Error("Generation timed out after 5 minutes. Please try again.");
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

        // Poll shouldn't retry aggressively — use a short timeout, ignore 502s from a single flake
        let statusRes;
        try {
          statusRes = await api.get(`/generate-cycle/status/${jobId}`, {
            headers, timeout: 15000,
          });
        } catch (pollErr) {
          // Transient network hiccup — keep polling until MAX_WAIT_MS
          console.warn("Poll failed, retrying:", pollErr?.message);
          continue;
        }

        const { status, result, error } = statusRes.data;
        if (status === "done") {
          finalResult = result;
          break;
        }
        if (status === "failed") {
          throw new Error(error || "Cycle generation failed on the server");
        }
      }

      localStorage.setItem("cycleResults", JSON.stringify(finalResult));
      navigate("/results");
      toast.success(t("cycleGenerated"));
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || t("failedToGenerate");
      toast.error(detail);
      console.error("Generate cycle failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] grid-pattern">
      <nav className="absolute top-0 left-0 right-0 z-50 py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <div className="text-base sm:text-xl font-bold text-white truncate">
            PeptidePlanner<span className="text-cyan-400">AI</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <LanguageSwitcher />

            {isAuthenticated && user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 text-zinc-300 hover:text-white px-2 sm:px-3" data-testid="user-menu">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-cyan-400" />
                      </div>
                    )}
                    <span className="hidden sm:inline">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-zinc-950 border-zinc-800">
                  <DropdownMenuItem
                    onClick={() => navigate("/dashboard")}
                    className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {t("dashboard")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem
                    onClick={() => { logout(); toast.success(t("loggedOut")); }}
                    className="text-red-400 hover:text-red-300 hover:bg-zinc-800 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t("logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </nav>

      {/* Attention-grabbing 30-day free trial banner — first thing users see */}
      <div className="pt-16 sm:pt-20">
        <TrialBanner onCTAClick={() => navigate("/subscription")} />
      </div>

      <section className="relative flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 hero-bg opacity-20"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1738510992707-6892b7dcc8a2?auto=format&fit=crop&w=1920&q=80')" }}
        />
        <div className="absolute inset-0 hero-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-12 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-6">
              <Zap className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-cyan-400 font-medium">{t("aiPoweredPlanning")}</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              <span className="text-white">{t("designYourPerfect")}</span>
              <br />
              <span className="gradient-text">{t("peptideCycle")}</span>
            </h1>

            <p className="text-lg text-zinc-400 max-w-2xl mx-auto mb-8">{t("heroDescription")}</p>
          </div>

          <GoalForm
            goals={goals}
            selectedGoals={selectedGoals}
            toggleGoal={toggleGoal}
            customGoal={customGoal}
            setCustomGoal={setCustomGoal}
            weight={weight}
            setWeight={setWeight}
            weightUnit={weightUnit}
            setWeightUnit={handleUnitChange}
            experience={experience}
            setExperience={setExperience}
            loading={loading}
            handleSubmit={handleSubmit}
            t={t}
          />
        </div>
      </section>

      <FeaturesSection features={features} t={t} />
      <HowItWorksSection steps={steps} t={t} />
      <TrustSection t={t} />
      <PricingSection language={language} currencySymbol={currencySymbol} navigate={navigate} />

      <GenerationOverlay visible={loading} language={language} />

      <footer className="py-8 px-4 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-zinc-500 text-sm">
            © 2025 PeptidePlanner AI. {t("forResearch")}
          </p>
        </div>
      </footer>
    </div>
  );
}
